import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ErroApi } from '../api/cliente';
import type { Precos } from '../api/tipos';
import { useSessao } from '../auth/SessaoContext';
import { rotaInicial } from '../auth/rotas';
import { Aviso, Botao, Campo } from '../componentes/ui';
import { Gift, RefreshCw } from 'lucide-react';
import { CabecalhoAuth, MolduraAuth } from './MolduraAuth';
import css from './Auth.module.css';
import { soDigitos } from '../util/mascaras';
import { reais } from '../util/formato';

/** Carência do backend (`VERIFICACAO_CARENCIA_SEG`), espelhada no botão. */
const CARENCIA_PADRAO_SEG = 60;

export function VerificarEmail() {
  const { usuario, recarregar, sair } = useSessao();
  const navegar = useNavigate();

  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [espera, setEspera] = useState(CARENCIA_PADRAO_SEG);
  const campo = useRef<HTMLInputElement>(null);

  // Já verificado (outra aba, ou voltou pela URL): não faz sentido ficar aqui.
  useEffect(() => {
    if (usuario?.emailVerificadoEm) {
      navegar(rotaInicial(usuario), { replace: true });
    }
  }, [usuario, navegar]);

  // Contagem regressiva do botão de reenvio.
  useEffect(() => {
    if (espera <= 0) return;
    const t = window.setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setAviso(null);
    setEnviando(true);
    try {
      const r = await api<{ jaEra: boolean; bonusCentavos: number }>(
        '/auth/verificar-email',
        { metodo: 'POST', corpo: { codigo: soDigitos(codigo) } },
      );
      // Recarrega o perfil para o resto do painel enxergar a conta verificada.
      await recarregar();
      // O valor vem do servidor, não de um número escrito aqui: se a promoção
      // mudar ou for desligada, a tela acompanha sem deploy.
      navegar(rotaInicial(usuario), {
        replace: true,
        state: r.bonusCentavos > 0 ? { bonus: r.bonusCentavos } : undefined,
      });
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível confirmar o código.',
      );
      setCodigo('');
      campo.current?.focus();
    } finally {
      setEnviando(false);
    }
  }

  async function reenviar() {
    setErro(null);
    setAviso(null);
    try {
      await api<{ expiraEm: string }>('/auth/reenviar-codigo', {
        metodo: 'POST',
        corpo: {},
      });
      setAviso('Enviamos um código novo. O anterior deixou de valer.');
      setEspera(CARENCIA_PADRAO_SEG);
    } catch (err) {
      if (err instanceof ErroApi && err.status === 429) {
        // A mensagem do servidor traz os segundos restantes.
        setAviso(err.message);
        const seg = Number(
          /(\d+)s/.exec(err.message)?.[1] ?? CARENCIA_PADRAO_SEG,
        );
        setEspera(seg);
      } else {
        setErro(
          err instanceof ErroApi
            ? err.message
            : 'Não foi possível reenviar o código.',
        );
      }
    }
  }

  return (
    <MolduraAuth>
      <form onSubmit={confirmar} noValidate className={css.pilha}>
        <CabecalhoAuth titulo="Confirme seu e-mail">
          Enviamos um código de 6 dígitos para <b>{usuario?.email}</b>.
        </CabecalhoAuth>

        <BonusDeBoasVindas />

        {erro && <Aviso>{erro}</Aviso>}
        {aviso && <Aviso tom="info">{aviso}</Aviso>}

        <Campo
          ref={campo}
          rotulo="Código de verificação"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          autoFocus
          maxLength={7}
          value={codigo}
          onChange={(e) => setCodigo(soDigitos(e.target.value).slice(0, 6))}
          placeholder="000000"
          className={css.codigo}
        />

        <Botao
          type="submit"
          larguraTotal
          grande
          carregando={enviando}
          disabled={codigo.length !== 6}
        >
          Confirmar
        </Botao>

        {/* ── Não recebeu? ──────────────────────────────────────────────
            Bloco próprio, separado por régua: é o caminho alternativo, e
            misturá-lo com a ação principal fazia tudo virar um monte de texto
            cinza indistinguível. */}
        <div className={css.divisoria}>
          <span className={css.notaCentral}>
            Não recebeu? Confira a caixa de spam.
          </span>

          <Botao
            aparencia="secundario"
            larguraTotal
            onClick={() => void reenviar()}
            disabled={espera > 0}
            icone={<RefreshCw />}
          >
            {espera > 0 ? `Reenviar em ${espera}s` : 'Reenviar código'}
          </Botao>
        </div>

        {/* ── Sair desta tela ───────────────────────────────────────────── */}
        <div className={css.divisoria}>
          <span className={css.notaDiscreta}>
            Você já pode usar o painel — só as buscas ficam bloqueadas até
            confirmar.
          </span>
          <div className={css.acoes}>
            <Botao
              aparencia="secundario"
              larguraTotal
              onClick={() => navegar(rotaInicial(usuario))}
            >
              Ir para o painel
            </Botao>
            <Botao
              aparencia="discreto"
              onClick={() => void sair().then(() => navegar('/'))}
            >
              Sair
            </Botao>
          </div>
        </div>
      </form>
    </MolduraAuth>
  );
}

/**
 * Lembra o bônus na hora em que ele é o incentivo — a tela do código.
 *
 * Some sozinho quando a promoção está desligada, porque o valor vem de
 * `GET /creditos/precos`. Um texto fixo aqui continuaria prometendo R$ 20
 * depois de a campanha acabar.
 */
function BonusDeBoasVindas() {
  const [centavos, setCentavos] = useState(0);

  useEffect(() => {
    api<Precos>('/creditos/precos')
      .then((p) => setCentavos(p.bonusCadastroCentavos))
      .catch(() => undefined);
  }, []);

  if (centavos <= 0) return null;

  return (
    <div className={css.bonus}>
      <Gift aria-hidden="true" />
      <div>
        <strong>{reais(centavos)} em créditos ao confirmar</strong>
        <span>
          Entram na conta assim que o código for validado — dá para começar a
          baixar sem pagar nada.
        </span>
      </div>
    </div>
  );
}
